<template>
  <div class="category-selector is-flex">
    <div class="dropdown" :class="{'is-active': showCategories}" >
      <div class="dropdown-trigger" :class="{'cursor-pointer': !exceededMaxCategories}">
        <div class="is-flex" tabindex="0" @keypress.enter.space="showCategories = !showCategories" @click="showCategories = !showCategories" v-click-outside="clickOutside">
          <div class="category-button has-text-grey is-flex">
            <span v-if="showCategoryButton" class="icon plus-btn" qa="add-cat-btn" tabindex="0">
              <i v-if="!showCategories" class="fal fa-plus-circle has-text-primary" aria-hidden="true"></i>
              <i v-else class="fal fa-times" aria-hidden="true"></i>
            </span>
            <span v-if="!isAdminMenu" class="is-uppercase">{{ $t("projectPage.categories") }}</span>
            <span v-else class="is-uppercase">{{ $t("projectPage.choose") }}</span>
          </div>
        </div>
      </div>
      <div class="dropdown-menu" id="dropdown-menu" role="menu" >
        <div class="dropdown-content" v-if="!hasFairID">
          <template v-for="category in allCategories">
            <span v-if="!category.isUnselectable || showAllCategories" :key="category.id">
              <div v-if="includeSubCategories && category.subCategories && category.subCategories.length > 0">
                {{category.name}}
              </div>
              <a v-else class="dropdown-item" :class="{'has-text-weight-bold': includeSubCategories}" tabindex="0" @keypress.enter.space="addCategory(category)" @click="addCategory(category)" :key="category.id">
                {{category.name}}
              </a>
              <template v-if="includeSubCategories">
                <span v-for="subCategory in category.subCategories" :key="subCategory.id">
                  <span v-if="!subCategory.isUnselectable">
                    <a class="dropdown-item subcategory" @click="addCategory(subCategory)">
                      {{subCategory.name}}
                    </a>
                  </span>
                </span>
              </template>
            </span>
          </template>
        </div>
      </div>
    </div>
    <div v-for="(category, offset) in chosenCategories" :class="{'has-categories' : chosenCategories && chosenCategories.length, 'is-profile' : isProfile, 'big-tag': isModal}" :key="offset" class="tag unselectable" :data-category="category" :data-offset="offset" draggable="true" @dragstart="drag" @dragover="allowDrop" @drop="drop">
      {{projectCategoriesNames(category.id)}}
      <button class="delete is-small" tabindex="0" @keypress.enter.space="removeCategory(category)" @click="removeCategory(category)"></button>
    </div>
    <div v-if="error && chosenCategories.length >= maxCategorySelection" class="help has-text-grey">{{error}}</div>
  </div>
</template>

<script>
import ClickOutside from 'vue-click-outside'
export default {
  props: ['value', 'isProfile', 'includeSubCategories', 'isAdminMenu', 'isModal', 'fairID', 'showAllCategories', 'project'],
  data: function () {
    return {
      showCategories: false,
      dragging: { to: null, from: null },
      isFocusedInput: false,
      chosenCategories: this.value.slice(),
      error: '',
      showError: false
    }
  },
  watch: {
    chosenCategories: function () {
      this.$emit('input', this.chosenCategories)
    },
    value: function () {
      if (this.value && this.value.length !== this.chosenCategories.length) {
        this.chosenCategories = this.value.slice()
      }
    }
  },
  mounted: function () {
    // prevent click outside event with popupItem. Always add it in mounted according documentation https://github.com/vue-bulma/click-outside#readme -vueclick-outside
    this.popupItem = this.$el
  },
  computed: {
    allCategories: function () {
      return this.$store.getters.categoriesLoaded ? this.$store.getters.sortedProjectCategories(this.includeSubCategories) : []
    },
    maxCategorySelection: function () {
      return this.$isYSC || this.$isAjas ? 1 : 3
    },
    exceededMaxCategories: function () {
      return this.chosenCategories.length >= this.maxCategorySelection
    },
    showCategoryButton: function () {
      return !this.exceededMaxCategories && !this.hasFairID
    },
    hasFairID: function () {
      return this.$isIsef && this.fairID !== ''
    }
  },
  created: function () {
    let categorySlug = ''
    if (this.project.sourceProjectID === 92391) {
      categorySlug = 'scienceday'
    }
    this.$store.dispatch('getCategories', categorySlug)
  },
  methods: {
    addCategory: function (category) {
      if (this.exceededMaxCategories) {
        this.error = 'You have reached your category selection limit. Remove a category if you would like to add a new selection.'
        return
      }
      const newCategoryName = category.name
      const matchedTags = this.chosenCategories.filter((category) => { return category.name === newCategoryName })
      if (matchedTags.length !== 0) {
        return
      }
      this.chosenCategories.push(category)
      this.showCategories = false
      this.$emit('calculateBodyHeight')
    },
    removeCategory: function (category) {
      this.chosenCategories.splice(this.chosenCategories.indexOf(category), 1)
    },
    drag: function (event) {
      this.dragging.from = parseInt(event.target.dataset.offset)
    },
    canDrop: function (to) {
      return !isNaN(to) && this.dragging.from !== null && this.dragging.from !== to
    },
    allowDrop: function (event) {
      const to = parseInt(event.target.dataset.offset)
      if (this.canDrop(to)) {
        event.preventDefault()
        this.dragging.to = to
        this.drop(event)
      }
    },
    drop: function (event) {
      event.preventDefault()
      this.chosenCategories.splice(this.dragging.to, 0, this.chosenCategories.splice(this.dragging.from, 1)[0])
      this.dragging.from = this.dragging.to
      this.dragging.to = null
    },
    clickOutside: function () {
      this.showCategories = false
    },
    projectCategoriesNames: function (id) {
      return this.$store.getters.nameCategoryByID(id)
    }
  },
  directives: {
    ClickOutside
  }
}
</script>

<style lang="scss" scoped>
  @import '~styles/utils.scss';
  .category-selector {
    width: initial;
    flex-wrap: wrap;
    align-items: center;
    .category-button {
      margin: 0.5rem 0;
      margin-right: 0.5rem;
      font-size: 0.7rem;
      align-items: center;
      .icon {
        margin-right: 0.5rem;
        font-size: 1.25rem;
      }
    }
  }
  .tag {
    font-size: 0.6rem;
    color: white;
    border-radius: 0;
    margin-right: .5em;
    &.big-tag {
      font-size: 0.8rem;
      margin-bottom: 0.5rem;
    }
  }
  .is-profile {
    .tag {
      font-size: 0.8rem;
    }
  }
  .dropdown-item {
    padding-left: 0.25rem;
    font-size: 0.8rem;
    line-height: 1rem;
    &.subcategory {
      padding-left: 0.5rem;
    }
  }
  .dropdown-content {
    min-height: 27vh;
    max-height: 40vh;
    overflow-y: auto;
    @include mobile {
      max-height: 30vh;
    }
    div {
      font-weight: bold;
      padding-left: 0.25rem;
      line-height: 1.5rem;
      font-size: 0.8rem;
    }
  }
  .admin-toolbar { // class is applied externally
    .dropdown {
      margin-bottom: 0.3rem;
    }
    .dropdown-trigger {
      margin-right: 0;
    }
    .tag {
      margin: 0.15rem 0.3rem 0.15rem 0;
    }
  }
  .plus-btn {
    i {
      font-size: 1.5rem;
    }
  }
</style>
